import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Button } from '@material-ui/core';
import { Check, ExitToApp } from '@material-ui/icons';
import { Query } from '@material-table/core';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';
import { IGridQueryRequest, OrderDirection } from '../../../../../Api/AptorApi';
import { useAptorApi } from '../../../../../Api';
import { Container, MTable } from '../../../../../Components';
import { AccessLevel, AdminFeature, UserContext } from '../../../../../Context/UserContext/UserContext';
import { ImpersonationListFilter } from './ImpersonationListFilter';
import { useStyles } from './ImpersonationsTable.styles';

export type ImpersonationState = 'active' | 'expired' | 'inactive';

type ImpersonationRow = {
  id: string;
  userEmail: string;
  userName: string;
  reason: string;
  status: 'pending' | 'accepted' | 'active' | 'rejected' | 'expired' | 'completed';
  requestedAtUTC: Date;
  respondBeforeUTC: Date;
  accessValidToUTC?: Date;
};

export const ImpersonationsTable = () => {
  const classes = useStyles();
  const { api } = useAptorApi();
  const { formatMessage } = useIntl();
  const { impersonateUser, hasAccessToFeature } = useContext(UserContext);
  const navigate = useNavigate();
  const tableRef = React.useRef<any>(null);
  const [status, setStatus] = useState<ImpersonationState>('active');
  const canManage = hasAccessToFeature(AdminFeature.Administrate, AccessLevel.Manage);

  const fetchRows = useCallback(async (query: Query<ImpersonationRow>) => api.searchImpersonationRequests<ImpersonationRow>({
    orderDirection: query.orderDirection === 'asc' ? OrderDirection.Ascending : OrderDirection.Descending,
    orderBy: query.orderBy ? query.orderBy.field : 'requestedAtUTC',
    page: query.page,
    pageSize: query.pageSize,
    search: query.search,
    filters: { state: [status] },
  } as IGridQueryRequest), [api, status]);

  useEffect(() => {
    tableRef.current?.onQueryChange();
  }, [status]);

  const isAccessible = (row: ImpersonationRow) => row.status === 'active' || row.status === 'accepted';

  return (
    <Container fullWidth>
      <Box className={classes.toolbar}>
        <ImpersonationListFilter value={status} onChange={setStatus} />
        {canManage && (
          <Button component={Link} to="/admin/law-portal/support-invites/new" variant="outlined">
            {formatMessage({ id: 'admin.impersonation-request.add' })}
          </Button>
        )}
      </Box>
      <MTable
        tableRef={tableRef}
        columns={[
          { title: formatMessage({ id: 'admin.support.table.user' }), field: 'userName', render: (row: any) => `${row.userName} <${row.userEmail}>` },
          { title: formatMessage({ id: 'dashboard.impersonation.reason' }), field: 'reason', sorting: false },
          { title: formatMessage({ id: 'utils.filters.status' }), field: 'status' },
          { title: formatMessage({ id: 'admin.impersonations.table.requested' }), field: 'requestedAtUTC', render: (row: any) => moment.utc(row.requestedAtUTC).local().format('lll') },
          { title: formatMessage({ id: 'admin.impersonations.table.validTo' }), field: 'accessValidToUTC', render: (row: any) => row.accessValidToUTC ? moment.utc(row.accessValidToUTC).local().format('lll') : '—' },
          {
            title: '',
            field: 'actions',
            sorting: false,
            render: (row: any) => canManage && isAccessible(row) ? (
              <Box className={classes.rowActions}>
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  startIcon={<ExitToApp />}
                  onClick={(event) => {
                    event.stopPropagation();
                    impersonateUser(row.userEmail);
                    navigate('/');
                  }}
                >
                  {formatMessage({ id: 'admin.support.table.impersonate' })}
                </Button>
                <Button
                  size="small"
                  color="primary"
                  variant="outlined"
                  startIcon={<Check />}
                  onClick={async (event) => {
                    event.stopPropagation();
                    await api.completeImpersonationRequest(row.id);
                    tableRef.current?.onQueryChange();
                  }}
                >
                  {formatMessage({ id: 'dashboard.impersonation.complete' })}
                </Button>
              </Box>
            ) : null,
          },
        ]}
        data={fetchRows}
      />
    </Container>
  );
};
